import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@material-ui/core";
import React, { useCallback, useContext, useMemo, useState } from "react";

import { Site } from "../../../../../dtos/site";
import { RequestState } from "../../../../../enums/requestState";
import SignalRContext from "../../../../../signalr/context";
import { useApi } from "../../../../useApi";

import { actionButtonStyle } from "./actionButtonStyle";
import { useRestartAction } from "./useRestartAction";

const buttonLabels: { [key in RequestState]: string; } = {
    [RequestState.Idle]: "Change",
    [RequestState.Requesting]: "Requesting...",
    [RequestState.Processing]: "Changing password...",
}

const MIN_LENGTH = 8;

/** Mirrors the client-side complexity hint on ACS's own change-password page. */
function getPasswordComplexityErrors(password: string): string[] {
    const errors: string[] = [];
    if (password.length < MIN_LENGTH) {
        errors.push(`at least ${MIN_LENGTH} characters`);
    }
    if (!/[a-z]/.test(password)) {
        errors.push("a lower case letter");
    }
    if (!/[A-Z]/.test(password)) {
        errors.push("an upper case letter");
    }
    if (!/[0-9]/.test(password)) {
        errors.push("a number");
    }
    if (!/[^a-zA-Z0-9]/.test(password)) {
        errors.push("a symbol");
    }
    return errors;
}

interface Props {
    site: Site;
    /** The button - and the dialog's submit action - stay disabled while the site is offline. */
    isSiteOnline: boolean;
}

export function ChangePasswordButton({ site, isSiteOnline }: Props) {
    const { siteApi } = useApi();
    const { changePasswordCompleted } = useContext(SignalRContext);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const request = useCallback(
        () => siteApi.changePassword(site.id, oldPassword, newPassword),
        [siteApi, site.id, oldPassword, newPassword],
    );

    const { requestState, trigger } = useRestartAction({
        siteId: site.id,
        request,
        ack: changePasswordCompleted,
        requestErrorMessage: "ERROR: Failed to request password change",
        successMessage: "Password changed",
        failureMessage: "ERROR: Failed to change password - the current password may be incorrect",
    });

    const complexityErrors = useMemo(() => getPasswordComplexityErrors(newPassword), [newPassword]);
    const passwordsMatch = confirmPassword.length === 0 || confirmPassword === newPassword;
    const isValid = oldPassword.length > 0
        && newPassword.length > 0
        && confirmPassword.length > 0
        && newPassword === confirmPassword
        && complexityErrors.length === 0;

    const resetForm = useCallback(() => {
        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");
    }, []);

    const openDialog = useCallback(() => setDialogOpen(true), []);
    const closeDialog = useCallback(() => {
        setDialogOpen(false);
        resetForm();
    }, [resetForm]);

    const handleSubmit = useCallback(() => {
        closeDialog();
        trigger();
    }, [closeDialog, trigger]);

    return (
        <>
            <Button
                color="primary"
                variant="contained"
                onClick={openDialog}
                disabled={!isSiteOnline || requestState !== RequestState.Idle}
                style={actionButtonStyle}
            >
                {buttonLabels[requestState]}
            </Button>

            <Dialog open={dialogOpen} onClose={closeDialog}>
                <DialogTitle>Change device password</DialogTitle>
                <DialogContent style={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "320px" }}>
                    <TextField
                        id="change-password-old"
                        label="Current password"
                        type="password"
                        value={oldPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
                        fullWidth
                    />
                    <TextField
                        id="change-password-new"
                        label="New password"
                        type="password"
                        value={newPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
                        error={newPassword.length > 0 && complexityErrors.length > 0}
                        helperText={
                            newPassword.length > 0 && complexityErrors.length > 0
                                ? `Must contain ${complexityErrors.join(", ")}`
                                : " "
                        }
                        fullWidth
                    />
                    <TextField
                        id="change-password-confirm"
                        label="Confirm new password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
                        error={!passwordsMatch}
                        helperText={!passwordsMatch ? "Does not match new password" : " "}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} disabled={!isValid || !isSiteOnline} color="primary">
                        Change password
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
